import React, { useEffect, useState } from "react";
import { LineChart, Line, AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";
import { ChartLine, TrendUp, Eye } from "@phosphor-icons/react";
import { api } from "../lib/api";

export default function Analytics() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then(r => setData(r.data)); }, []);

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="analytics-page">
      <p className="text-[#FF3B6B] text-sm font-mono">// ANALYTICS</p>
      <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Performance signals</h1>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        {[
          { label: "Projects", value: data?.projects ?? 0 },
          { label: "Predicted reach", value: (data?.predicted_reach ?? 0).toLocaleString() },
          { label: "Avg viral score", value: (data?.avg_viral_score ?? 0) + "/100", accent: true },
          { label: "Engagement (7d)", value: "+24%" },
        ].map((s, i) => (
          <div key={i} className="glass rounded-xl p-5" data-testid={`analytics-stat-${i}`}>
            <p className="text-xs text-white/50 uppercase tracking-wider">{s.label}</p>
            <p className={`mt-2 font-mono text-2xl ${s.accent ? "text-[#FF3B6B]" : ""}`}>{s.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 glass rounded-xl p-6" data-testid="analytics-views-chart">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold flex items-center gap-2"><Eye className="text-[#FF3B6B]" /> Views (7d)</h2>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.series || []}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FF3B6B" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#FF3B6B" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.4)" fontSize={12} />
                <YAxis stroke="rgba(255,255,255,0.4)" fontSize={12} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Area type="monotone" dataKey="views" stroke="#FF3B6B" fill="url(#g1)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass rounded-xl p-6" data-testid="analytics-engagement-chart">
          <h2 className="font-semibold flex items-center gap-2 mb-4"><TrendUp className="text-[#00E5FF]" /> Engagement</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data?.series || []}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.4)" fontSize={12} />
                <Tooltip contentStyle={{ background: "#121214", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
                <Bar dataKey="engagement" fill="#00E5FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="mt-6 glass rounded-xl p-6" data-testid="analytics-prediction">
        <h2 className="font-semibold mb-2 flex items-center gap-2"><ChartLine className="text-[#EAB308]" /> Engagement prediction</h2>
        <p className="text-white/60 text-sm">Based on your viral scores, retention curves, and platform trends — your next post is projected to outperform 67% of your library.</p>
      </div>
    </div>
  );
}
